import { useRef } from 'react';
import { useI18n } from 'next-localization';

import { LinkButton } from '@/components/link-button';
import { useModalContext } from '@/components/modals/modal-context';
import { ProviderLocation } from '@/features/profile/profile.types';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { useModalFocusManagement } from '@/hooks/use-modal-focus-management';
import { useProviderLocation } from '@/hooks/use-provider-location';
import { cn } from '@/lib/utils';

import styles from '../../listing.module.css';

type AddressSectionProps = {
  providerLocations: ProviderLocation[];
  providerId: string;
  activeLocation: ProviderLocation;
};

export const AddressSection = ({
  providerLocations,
  providerId,
  activeLocation,
}: AddressSectionProps): JSX.Element => {
  const { t } = useI18n();
  const { openModal, isOpen } = useModalContext();
  const { setProviderLocations, setActiveDisplayLocation, setActiveProviderId } =
    useDistanceContext();

  const linkButtonRef = useRef<HTMLButtonElement>(null);
  const { handleOpenModal } = useModalFocusManagement({
    modalName: 'change-location-list-modal',
    isModalOpen: isOpen,
    fallbackElementRef: linkButtonRef,
    onBeforeOpen: () => {
      // Set up modal data before opening
      setProviderLocations(providerLocations);
      setActiveProviderId(providerId);
      setActiveDisplayLocation(activeLocation);
    },
  });

  const getProviderActiveLocation = useProviderLocation();
  const location = getProviderActiveLocation(providerLocations, providerId);

  return (
    <div className={cn('col-lg-4 col-md-4 d-flex', styles.addressSectionWrapper)}>
      <div>
        <h5 className={styles.addressCardTitle}>{t('OAS.Profile.Details.Address')}</h5>
        <p className={cn('mb-0', styles.addressName)}>
          <span className="sr-only">{t('OAS.Profile.Details.Address')}: </span>
          <span style={{ fontWeight: 600 }}>{location.name}</span>
          <br />
          {location.street}
          <br />
          {location.city}, {location.state} {location.zip}
          <br />
        </p>
        <div className="d-flex align-items-center flex-wrap pt-1">
          {providerLocations?.length > 0 && (
            <LinkButton
              ref={linkButtonRef}
              onClick={() => handleOpenModal(() => openModal('change-location-list-modal'))}
              className={cn('d-flex px-0 d-flex align-items-center', styles.linkButton)}
              type="button"
            >
              <span>
                {providerLocations?.length > 1
                  ? `${t('OAS.Common.ChangeLocation')} (${providerLocations?.length})`
                  : t('OAS.Listing.LocationDetails')}
              </span>
            </LinkButton>
          )}
        </div>
      </div>
    </div>
  );
};
